<template>
    <div class="refill-in-batch">
        <b-alert v-if="errorMessage" variant="danger" show dismissible @dismissed="errorMessage = ''">
            {{ errorMessage }}
        </b-alert>

        <div v-if="loading" class="text-center py-5">
            <b-spinner label="Loading..."></b-spinner>
            <div class="mt-2">Calculating short fills...</div>
        </div>

        <template v-else>
            <b-row>
                <b-col>
                    <p class="text-muted">
                        The following prescriptions need to be filled with adjusted Days Supply to synchronize
                        with the anchor date of <strong>{{ formatDate(anchorDate) }}</strong>.
                        These short fills will be automatically tagged with SCC 47 (Shortened Days Supply Fill).
                    </p>
                </b-col>
            </b-row>

            <hr>

            <!-- Short Fill Prescriptions Table -->
            <b-row class="mb-3">
                <b-col>
                    <h5>Short Fill Prescriptions</h5>

                    <div v-if="shortFillItems.length === 0" class="text-center text-muted py-4">
                        <i class="fa fa-info-circle fa-2x mb-2"></i>
                        <p>No prescriptions require short fills.</p>
                    </div>

                    <b-table
                        v-else
                        :items="shortFillItems"
                        :fields="shortFillFields"
                        hover
                        class="mb-0">

                        <template #cell(medication)="data">
                            <div>
                                <strong>{{ data.item.rxID }}</strong><br>
                                {{ data.item.drugName }}
                            </div>
                        </template>

                        <template #cell(originalDaysSupply)="data">
                            <span class="text-muted">{{ data.item.originalDaysSupply }} days</span>
                        </template>

                        <template #cell(adjustedDaysSupply)="data">
                            <b-form-input
                                v-model.number="data.item.adjustedDaysSupply"
                                type="number"
                                min="1"
                                :max="data.item.originalDaysSupply"
                                size="sm"
                                style="width: 80px;"
                                @input="recalculateDispensed(data.item)">
                            </b-form-input>
                        </template>

                        <template #cell(originalDispensed)="data">
                            <span class="text-muted">{{ data.item.originalDispensed }}</span>
                        </template>

                        <template #cell(adjustedDispensed)="data">
                            <span class="text-success font-weight-bold">{{ data.item.adjustedDispensed }}</span>
                        </template>

                        <template #cell(nextFillDate)="data">
                            {{ formatDate(data.item.nextFillDate) }}
                        </template>

                        <template #cell(alignmentDate)="data">
                            <span class="text-success">{{ formatDate(anchorDate) }}</span>
                        </template>
                    </b-table>

                    <div v-if="shortFillItems.length > 0" class="mt-2">
                        <small class="text-info">
                            <i class="fa fa-info-circle"></i>
                            Adjust Days Supply values as needed. Quantities will be automatically calculated based on the ratio of days supply.
                        </small>
                    </div>
                </b-col>
            </b-row>

            <hr>

            <!-- Summary Information -->
            <b-row class="mb-3">
                <b-col>
                    <b-card bg-variant="light">
                        <h6 class="mb-2">Processing Summary</h6>
                        <ul class="mb-0">
                            <li><strong>Patient:</strong> {{ patient.displayNamesForPerson() }}</li>
                            <li><strong>Anchor Date:</strong> {{ formatDate(anchorDate) }}</li>
                            <li><strong>Short Fills:</strong> {{ shortFillItems.length }} prescription(s)</li>
                            <li><strong>Submission Clarification Code:</strong> 47 (Shortened Days Supply Fill)</li>
                        </ul>
                    </b-card>
                </b-col>
            </b-row>

            <hr>

            <!-- Action Buttons -->
            <b-row>
                <b-col class="text-right">
                    <b-button variant="secondary" @click="cancel" class="mr-2">
                        Cancel
                    </b-button>
                    <b-button
                        variant="success"
                        @click="startProcessing"
                        :disabled="shortFillItems.length === 0">
                        Start Processing Short Fills
                    </b-button>
                </b-col>
            </b-row>

            <!-- PRESCRIPTION REFILL MODAL -->
            <b-modal id="short-fill-rx-modal" size="xl" :title="currentPrescriptionTitle" hide-footer no-close-on-backdrop>
                <RxPage
                    :refillBatchMode="true"
                    :prescriptionsInBatch="prescriptionsForBatch"
                    :medSyncShortFillData="currentShortFillData"
                    @batchFinished="onPrescriptionCompleted"
                    :card="false">
                </RxPage>

                <hr>

                <b-button variant="dark" @click="closePrescriptionModal">
                    Cancel
                </b-button>
            </b-modal>
        </template>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Patient } from '@/models/Patient';
import { Prescription } from '@/models/Prescription';
import { EligiblePrescription } from '@/models/MedSync';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface ShortFillItem {
    rxID: string;
    rxNumber: string;
    drugName: string;
    originalDaysSupply: number;
    adjustedDaysSupply: number;
    originalDispensed: number;
    adjustedDispensed: number;
    nextFillDate: Date | string;
    daysUntilAnchor: number;
}

@Component({
    name: 'RefillInBatchModal',
    components: {
        //Importing this way prevents component circular reference
        RxPage: () => import('@/pages/Prescription/PrescriptionDetailsPage.vue'),
    }
})
export default class RefillInBatchModal extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ required: true }) prescriptions!: (Prescription | EligiblePrescription)[];
    @Prop({ required: true }) anchorDate!: Date;

    // Data properties
    private loading = true;
    private processing = false;
    private errorMessage = '';
    private shortFillItems: ShortFillItem[] = [];
    private currentPrescriptionIndex = 0;
    private prescriptionsForBatch: (Prescription | EligiblePrescription)[] = [];
    private currentShortFillData: any = null;

    // Table fields configuration
    private shortFillFields = [
        { key: 'medication', label: 'Medication', tdClass: 'align-middle' },
        { key: 'originalDaysSupply', label: 'Original Days Supply', tdClass: 'align-middle text-center' },
        { key: 'adjustedDaysSupply', label: 'Adjusted Days Supply', tdClass: 'align-middle text-center' },
        { key: 'originalDispensed', label: 'Original Qty', tdClass: 'align-middle text-center' },
        { key: 'adjustedDispensed', label: 'Adjusted Qty', tdClass: 'align-middle text-center' },
        { key: 'nextFillDate', label: 'Current Next Fill', tdClass: 'align-middle' },
        { key: 'alignmentDate', label: 'Alignment Date', tdClass: 'align-middle' }
    ];

    async mounted() {
        await this.calculateShortFills();
    }

    async calculateShortFills() {
        this.loading = true;
        try {
            const anchorMoment = moment(this.anchorDate);

            this.shortFillItems = this.prescriptions
                .filter(rx => {
                    if (!rx.nextFillDate) return false;
                    const nextFill = moment(rx.nextFillDate);
                    return nextFill.isBefore(anchorMoment);
                })
                .map(rx => {
                    const nextFill = moment(rx.nextFillDate);

                    // Calculate adjusted days supply as the difference between next fill date and anchor date
                    // This is the number of days from the next fill date until the alignment date
                    const daysUntilAnchor = anchorMoment.diff(nextFill, 'days');
                    const adjustedDaysSupply = Math.max(1, daysUntilAnchor);

                    // Calculate adjusted dispensed amount based on ratio
                    const originalDaysSupply = rx.daySupply || 30;
                    const originalDispensed = rx.dispensed || rx.quantity || 0;
                    const ratio = adjustedDaysSupply / originalDaysSupply;
                    const adjustedDispensed = Math.ceil(originalDispensed * ratio);

                    return {
                        rxID: rx.rxID || '',
                        rxNumber: rx.rxNumber || '',
                        drugName: rx.drugName || rx.productNameShort || 'Unknown',
                        originalDaysSupply: originalDaysSupply,
                        adjustedDaysSupply: adjustedDaysSupply,
                        originalDispensed: originalDispensed,
                        adjustedDispensed: adjustedDispensed,
                        nextFillDate: rx.nextFillDate,
                        daysUntilAnchor: daysUntilAnchor
                    };
                });

        } catch (error) {
            console.error('Error calculating short fills:', error);
            this.errorMessage = 'Failed to calculate short fills. Please try again.';
        } finally {
            this.loading = false;
        }
    }

    formatDate(date: string | Date | null): string {
        if (!date) return 'N/A';
        return moment(date).format('MM/DD/YYYY');
    }

    recalculateDispensed(item: ShortFillItem) {
        // Recalculate adjusted dispensed amount when days supply changes
        const ratio = item.adjustedDaysSupply / item.originalDaysSupply;
        item.adjustedDispensed = Math.ceil(item.originalDispensed * ratio);
    }

    get currentPrescriptionTitle(): string {
        if (this.currentPrescriptionIndex >= this.shortFillItems.length) {
            return 'MedSync Short Fill';
        }
        const current = this.currentPrescriptionIndex + 1;
        const total = this.shortFillItems.length;
        const rx = this.shortFillItems[this.currentPrescriptionIndex];
        return `MedSync Short Fill (${current}/${total}) - ${rx.rxID}`;
    }

    startProcessing() {
        if (this.shortFillItems.length === 0) {
            this.errorMessage = 'No prescriptions to process.';
            return;
        }

        // Start with the first prescription
        this.currentPrescriptionIndex = 0;
        this.loadCurrentPrescription();

        // Open the prescription modal
        this.$bvModal.show('short-fill-rx-modal');
    }

    loadCurrentPrescription() {
        const currentItem = this.shortFillItems[this.currentPrescriptionIndex];
        const prescription = this.prescriptions.find(p => p.rxID === currentItem.rxID);

        if (prescription) {
            // Set the prescription for batch processing
            this.prescriptionsForBatch = [prescription];

            // Set the short fill data to be passed to RxPage
            this.currentShortFillData = {
                adjustedDaysSupply: currentItem.adjustedDaysSupply,
                adjustedDispensed: currentItem.adjustedDispensed,
                isMedSyncShortFill: true,
                medSyncAnchorDate: this.anchorDate
            };
        }
    }

    onPrescriptionCompleted() {
        // Move to the next prescription
        this.currentPrescriptionIndex++;

        // Check if there are more prescriptions to process
        if (this.currentPrescriptionIndex < this.shortFillItems.length) {
            // Load the next prescription
            this.loadCurrentPrescription();
        } else {
            // All prescriptions processed
            this.closePrescriptionModal();
            this.$notification(NotificationOptions.success(
                `Successfully processed ${this.shortFillItems.length} short fill prescription(s)`
            ));
            this.$emit('completed');
        }
    }

    closePrescriptionModal() {
        this.$bvModal.hide('short-fill-rx-modal');
    }

    cancel() {
        this.$emit('completed');
    }
}
</script>

<style scoped>
.refill-in-batch {
    padding: 20px;
}

.refill-in-batch h5 {
    color: #333;
    margin-bottom: 10px;
}
</style>
